import { useState } from "react";
import {
  Dialog,
  DialogFooter,
  DialogText,
  FieldLabel,
  RadioCards,
  ReasonField,
  TextArea,
  apiPost,
  errorText,
  formatDateTime,
  isApiError,
  useCommandMutation,
  useReason,
  useToast,
} from "@amber/ui";
import {
  PROBLEM_KIND_LABELS,
  REPORTED_PROBLEM_KINDS,
  problemResponseSchema,
  type EvidenceRecord,
  type ProblemView,
  type TurnView,
} from "../../contracts/index";
import { evidenceKeys } from "../keys";
import { communicationOf } from "./Editing";

const RESOLVE_NOTE = { minLength: 1, message: "Add a note: what was done, or why no change is needed" };

export function ResolveDialog({ problem, onClose }: { problem: ProblemView; onClose: () => void }) {
  const toast = useToast();
  const resolve = useCommandMutation({
    run: (csrf, text: string) =>
      apiPost(`/api/evidence/problems/${problem.id}/resolve`, { note: text }, problemResponseSchema, csrf),
    invalidate: [evidenceKeys.records],
    onSuccess: () => {
      toast.show("Report resolved; readiness reassessed");
      onClose();
    },
  });
  const note = useReason(resolve.error ? errorText(resolve.error, "Couldn't resolve the report") : null, RESOLVE_NOTE);
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Mark this report resolved?"
      onSubmit={() => {
        if (note.submit()) resolve.mutate(note.trimmed);
      }}
      footer={<DialogFooter onClose={onClose} confirm="Mark resolved" pending={resolve.isPending} />}
    >
      <DialogText>
        <b>{problem.kind_label}</b> · {problem.description}
        <br />
        <span className="text-muted">
          Reported by {problem.reported_by}, {formatDateTime(problem.reported_at)}
        </span>
      </DialogText>
      <ReasonField
        value={note.reason}
        onChange={(v) => {
          note.setReason(v);
          resolve.reset();
        }}
        error={note.error}
        placeholder="What was done, or why no change is needed"
      />
    </Dialog>
  );
}

export function ReportDialog({
  record,
  turn,
  onClose,
}: {
  record: EvidenceRecord;
  turn: TurnView | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const report = useCommandMutation({
    run: (csrf, body: unknown) =>
      apiPost(`/api/evidence/items/${record.item.id}/problems`, body, problemResponseSchema, csrf),
    invalidate: [evidenceKeys.records],
    onSuccess: () => {
      toast.show("Problem reported; an Editor task was created");
      onClose();
    },
  });
  const [about, setAbout] = useState<"passage" | "item">(turn?.unit_id ? "passage" : "item");
  const [kind, setKind] = useState<(typeof REPORTED_PROBLEM_KINDS)[number]>("wrong_text");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const com = turn ? communicationOf(record, turn) : null;
  return (
    <Dialog
      open
      wide
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Report a problem…"
      onSubmit={() => {
        if (description.trim().length === 0) {
          setError("Describe the problem");
          return;
        }
        const scope =
          about === "passage" && com && turn?.unit_id
            ? { kind: "passage", communication_id: com.id, unit_ids: [turn.unit_id] }
            : { kind: "item" };
        report.mutate(
          { scope, kind, description: description.trim() },
          {
            onError: (e) => {
              setError(isApiError(e) ? e.message : "Couldn't report the problem");
            },
          },
        );
      }}
      footer={<DialogFooter onClose={onClose} confirm="Report problem" pending={report.isPending} />}
    >
      <RadioCards
        label="About"
        value={about}
        onChange={setAbout}
        options={[
          ...(turn?.unit_id
            ? [
                {
                  value: "passage" as const,
                  label: "The selected passage",
                  description: `Turn ${String(turn.n)}: ${turn.words
                    .slice(0, 8)
                    .map((w) => w.text)
                    .join(" ")}…`,
                },
              ]
            : []),
          { value: "item" as const, label: "This item", description: record.item.title },
        ]}
      />
      <RadioCards
        label="Kind"
        value={kind}
        onChange={setKind}
        options={REPORTED_PROBLEM_KINDS.map((k) => ({ value: k, label: PROBLEM_KIND_LABELS[k] }))}
      />
      <div className="mt-3">
        <FieldLabel htmlFor="report-description" required>
          Description
        </FieldLabel>
        <TextArea
          id="report-description"
          value={description}
          aria-invalid={error ? true : undefined}
          onChange={(e) => {
            setDescription(e.target.value);
            setError(null);
          }}
          placeholder="What is wrong, and where"
        />
        {error ? (
          <p role="alert" className="m-0 mt-1.5 text-md text-attention">
            {error}
          </p>
        ) : null}
      </div>
      <DialogText>
        The report creates an Editor task and keeps the affected person's contributions Draft until an Editor resolves
        it.
      </DialogText>
    </Dialog>
  );
}
